import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { Contact } from './types';
import { contactService } from '../../../services/contactService';
import { useUserSelection } from '../../../contexts/UserSelectionContext';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('ContactsDashboard');

type RootStackParamList = {
  ContactsInputScreen: undefined;
  ContactsFamilyScreen: undefined;
  ContactsFriendsScreen: undefined;
  ContactsBusinessScreen: undefined;
  ContactsOtherScreen: undefined;
  ContactsEOLScreen: undefined;
  ContactsLegalScreen: undefined;
  ContactsMedicalScreen: undefined;
  ContactsPetScreen: undefined;
  ContactsPropertyScreen: undefined;
  ContactsSpiritualScreen: undefined;
  ContactsPersonalScreen: undefined;
  ContactsDataScreen: { contact: Contact };
};

type CategoryData = {
  id: string;
  name: string;
  icon: any;
  count: number;
  color: string;
  contacts: Contact[];
};

// Categories data with icons and counts
const categories: CategoryData[] = [
  { id: 'all', name: 'All', icon: require('../../../assets/icons/all-contacts.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'family', name: 'Family', icon: require('../../../assets/icons/family.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'friends', name: 'Friends', icon: require('../../../assets/icons/friends.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'medical', name: 'Medical', icon: require('../../../assets/icons/health.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'legal', name: 'Legal', icon: require('../../../assets/icons/legal.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'property', name: 'Property', icon: require('../../../assets/icons/property.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'business', name: 'Business', icon: require('../../../assets/icons/business.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'eol', name: 'EOL', icon: require('../../../assets/icons/eol.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'spiritual', name: 'Spiritual', icon: require('../../../assets/icons/spiritual.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'pet', name: 'Pet', icon: require('../../../assets/icons/pet.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'personal', name: 'Personal', icon: require('../../../assets/icons/user.png'), count: 0, color: '#FFFFFF', contacts: [] },
  { id: 'other', name: 'Other.', icon: require('../../../assets/icons/other-contacts.png'), count: 0, color: '#FFFFFF', contacts: [] },
];

// Contact data - now uses real API calls via contactService
// No longer needed as we fetch data dynamically

type CategoryCardProps = {
  item: CategoryData;
  isSelected: boolean;
  onPress: () => void;
  navigation: any;
  viewOnly?: boolean;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, onPress, navigation, viewOnly }) => {
  // Card click now filters the dashboard instead of navigating
  const handleCardPress = () => {
    onPress(); // This calls handleCategorySelect to filter the current dashboard
  };

  return (
    <TouchableOpacity
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#29BFC0' : '#FFFFFF' }
      ]}
      onPress={handleCardPress}
    >
      <View style={styles.categoryIconContainer}>
        <Image source={item.icon} style={styles.categoryIcon} />
      </View>
      <Text style={[
        styles.categoryName,
        { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
      ]}>{item.name}</Text>
      <Text style={[
        styles.categoryCount,
        { color: isSelected ? '#FFFFFF' : '#6B7280' }
      ]}>{item.count} {item.count === 1 ? 'Contact' : 'Contacts'}</Text>
      {!viewOnly && (
        <TouchableOpacity
          style={[
            styles.addButton,
            { backgroundColor: isSelected ? '#FFFFFF' : '#29BFC0' }
          ]}
          onPress={() => navigation.navigate('ContactsInputScreen' as any, { category: item.name })}
        >
          <Image
            source={require('../../../assets/icons/add.png')}
            style={[
              styles.addIcon,
              { tintColor: isSelected ? '#29BFC0' : '#FFFFFF' }
            ]}
          />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

type ContactItemProps = {
  item: Contact;
  onPress: (contact: Contact) => void;
};

const ContactItem: React.FC<ContactItemProps> = ({ item, onPress }) => {
  const getIcon = (category: string) => {
    const categoryData = categories.find(cat => cat.id === category);
    return categoryData ? categoryData.icon : categories[0].icon;
  };

  return (
    <TouchableOpacity 
      style={styles.contactItem}
      onPress={() => onPress(item)}
    >
      <View style={styles.contactIcon}>
        <Image source={getIcon(item.category)} style={styles.contactIconImage} />
      </View>
      <View style={styles.contactInfo}>
        <Text style={styles.contactName}>{`${item.firstName} ${item.lastName}`}</Text>
        <Text style={styles.contactPhone}>{item.phoneNumber}</Text>
      </View>
      <View style={styles.contactType}>
        <Text style={styles.contactRelationTop}>{item.relationship}</Text>
        <TouchableOpacity style={styles.moreButton}>
          <Text style={styles.moreButtonText}>•••</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

export const ContactsDashboardScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const { isViewingOthersData, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const [, setAllContacts] = useState<Contact[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState<CategoryData[]>(categories);
  const [searchText, setSearchText] = useState('');

  // Get route params for targetUserId and viewOnly
  const params = route.params as any;
  const targetUserId = params?.targetUserId || (isViewingOthersData ? selectedUserId : undefined);
  const viewOnly = params?.viewOnly || isViewingOthersData;

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Handle selectedCategory parameter from navigation
  useEffect(() => {
    if (params?.selectedCategory) {
      setSelectedCategory(params.selectedCategory);
    }
  }, [params?.selectedCategory]);

  useFocusEffect(
    useCallback(() => {
      loadContacts();
    }, [targetUserId])
  );

  const loadContacts = async () => {
    setLoading(true);
    try {
      // Fetch contacts from contactService (API with local storage fallback)
      // Pass targetUserId if viewing another user's data
      const contactsData = await contactService.getAllContacts(targetUserId);
      logger.debug('Loaded contacts from service:', contactsData.length, targetUserId ? `for user ${targetUserId}` : '');
      setAllContacts(contactsData);
      
      // Update category data with contact counts
      const updatedCategories = categories.map(category => {
        if (category.id === 'all') {
          return {
            ...category,
            count: contactsData.length,
            contacts: contactsData,
          };
        } else {
          const categoryContacts = contactsData.filter(c => c.category === category.id);
          return {
            ...category,
            count: categoryContacts.length,
            contacts: categoryContacts,
          };
        }
      });
      
      setCategoryData(updatedCategories);
    } catch (error) {
      logger.error('Error loading contacts:', error);
      Alert.alert('Error', 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleContactPress = (contact: Contact) => {
    navigation.navigate('ContactsDataScreen', { contact });
  };

  const handleAddContact = () => {
    navigation.navigate('ContactsInputScreen' as any);
  };

  const selectedCategoryData = categoryData.find(cat => cat.id === selectedCategory);
  const displayContacts = selectedCategoryData?.contacts || [];

  const filteredContacts = displayContacts.filter(contact =>
    searchText === '' ||
    `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(searchText.toLowerCase()) ||
    contact.relationship.toLowerCase().includes(searchText.toLowerCase()) ||
    contact.category.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading contacts...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder="Search contacts"
            placeholderTextColor="#9CA3AF"
            value={searchText}
            onChangeText={setSearchText}
          />
          <Image
            source={require('../../../assets/icons/search.png')}
            style={styles.searchIcon}
          />
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>{viewOnly ? 'View' : 'Manage'}</Text>
          <Text style={styles.allContactsText}>All Contacts</Text>
        </View>

        {/* View-only banner when viewing another user's data */}
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>
              Viewing {viewingUserName}'s Contacts (View Only)
            </Text>
          </View>
        )}

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoriesList}
          contentContainerStyle={{ paddingRight: 30 }}
        >
          {categoryData.map((category) => (
            <CategoryCard
              key={category.id}
              item={category}
              isSelected={selectedCategory === category.id}
              onPress={() => handleCategorySelect(category.id)}
              navigation={navigation}
              viewOnly={viewOnly}
            />
          ))}
        </ScrollView>

        <View style={styles.contactsContainer}>
          {filteredContacts.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                {selectedCategory === 'all' ? 'No Contacts Yet' : `No ${selectedCategoryData?.name} Contacts`}
              </Text>
              <Text style={styles.emptySubtitle}>
                {viewOnly
                  ? `${viewingUserName} has not added any ${selectedCategory === 'all' ? '' : selectedCategoryData?.name.toLowerCase() + ' '}contacts yet`
                  : selectedCategory === 'all'
                    ? 'Start by adding your first contact to keep them organized'
                    : `Add your first ${selectedCategoryData?.name.toLowerCase()} contact`
                }
              </Text>
              {!viewOnly && (
                <TouchableOpacity style={styles.mainAddButton} onPress={handleAddContact}>
                  <Text style={styles.addButtonText}>
                    {selectedCategory === 'all' ? 'Add Your First Contact' : `Add ${selectedCategoryData?.name} Contact`}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            filteredContacts.map((contact) => (
              <ContactItem key={contact.id} item={contact} onPress={handleContactPress} />
            ))
          )}
        </View>
      </ScrollView>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#1C2C4F',
    fontSize: ms(16, 0.3),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  searchContainer: {
    margin: ms(20),
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
  },
  searchInput: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  searchIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allContactsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: ms(25),
    paddingBottom: ms(10),
  },
  contactsContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  categoryCard: {
    width: s(130),
    height: s(160),
    borderRadius: ms(15),
    padding: ms(15),
    marginRight: ms(15),
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  categoryCount: {
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
  addButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    width: s(15),
    height: s(15),
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
    minHeight: s(400),
  },
  emptyTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(30),
  },
  mainAddButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  addButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    // Android
    elevation: 2,
    // iOS
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  contactIcon: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    tintColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactIconImage: {
    width: s(25),
    height: s(25),
  },
  contactInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  contactName: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
    marginBottom: ms(5),
  },
  contactPhone: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  contactType: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    minHeight: s(50),
  },
  contactRelationTop: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(2),
    textAlign: 'right',
  },
  moreButton: {
    padding: ms(5),
  },
  moreButtonText: {
    fontSize: ms(20, 0.3),
    color: '#6B7280',
    letterSpacing: ms(2),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(15),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
